import {
  DotsIcon,
  DotsWrapper,
  IconWrapper,
  PopupItem,
  PopupText,
  PopupWrapper,
  TablePopupWraper,
} from "./Styles";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

interface PopupProps {
  options: {
    icon: React.ReactNode;
    text: string;
    color?: string;
    onClick?: () => void;
  }[];
}

export const Popup: React.FC<PopupProps> = ({ options }) => {
  const [showPopUp, setShowPopup] = useState<boolean>(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popupWidth = 160; // 10rem = 160px
      setPosition({
        top: rect.bottom + 4,
        left: rect.right - popupWidth,
      });
    }
  }, []);

  useEffect(() => {
    if (showPopUp) {
      updatePosition();
    }
  }, [showPopUp, updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedInsideTrigger = triggerRef.current?.contains(target);
      const clickedInsidePopup = popupRef.current?.contains(target);

      if (!clickedInsideTrigger && !clickedInsidePopup) {
        setShowPopup(false);
      }
    }

    function handleScroll() {
      if (showPopUp) {
        setShowPopup(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopUp]);

  return (
    <PopupWrapper>
      <DotsWrapper ref={triggerRef} onClick={() => setShowPopup(!showPopUp)}>
        <DotsIcon />
      </DotsWrapper>
      {showPopUp &&
        createPortal(
          <TablePopupWraper ref={popupRef} $top={position.top} $left={position.left}>
            {options.map((option, index) => (
              <PopupItem
                key={`${option.text}-${index}`}
                hasCustomColor={!!option.color}
                onClick={() => {
                  if (option.onClick) {
                    option.onClick();
                  }
                  setShowPopup(false);
                }}
              >
                <IconWrapper color={option.color}>{option.icon}</IconWrapper>
                <PopupText color={option.color}>{option.text}</PopupText>
              </PopupItem>
            ))}
          </TablePopupWraper>,
          document.body
        )}
    </PopupWrapper>
  );
};
